/**
 * When it is safe to swap the bundle.
 *
 * **A reload between "terminal approved" and "order posted" is money taken for
 * an order nobody has.** So a waiting service worker does not activate on its
 * own schedule: it asks, and this answers.
 *
 * The shell's own updater has the same shape and for the same reason — Rust
 * downloads and holds, `shell://update-ready` tells the page, and
 * `shell_apply_update` installs only when the store can afford it. A passive
 * installer that restarted the app mid-basket is precisely the failure this
 * prevents, so the decision stays where the domain knowledge is.
 */
import { canInterrupt, canReloadOnRequest } from '@mgc/domain'
import { useKiosk } from '@/store/kiosk'
import { applyUpdate, closeVerdict, listen, reloadVerdict, updateStatus } from './native'

/** Whether *right now* is a moment the kiosk can lose its page. */
export function safeToInterrupt(paymentInFlight: boolean, modalOpen: boolean): boolean {
  const state = useKiosk.getState()
  return canInterrupt({
    cartEmpty: state.cart.lines.length === 0,
    paymentInFlight,
    modalOpen: modalOpen || state.openProduct !== null,
    onWelcomeScreen: state.step === 'welcome'
  })
}

interface Gate {
  paymentInFlight: boolean
  modalOpen: boolean
}

const gate: Gate = { paymentInFlight: false, modalOpen: false }

/** Called by the payment screen for the whole time money is in flight. */
export function holdUpdates(reason: keyof Gate, held: boolean): void {
  gate[reason] = held
}

export const currentlySafe = (): boolean => safeToInterrupt(gate.paymentInFlight, gate.modalOpen)

/**
 * Whether a reload an operator asked for can happen *now*.
 *
 * Deliberately not `currentlySafe`: `canReloadOnRequest` carries why a person
 * at the menu bar is not a service worker.
 */
export const reloadOnRequestSafe = (): boolean => canReloadOnRequest({ paymentInFlight: gate.paymentInFlight })

/**
 * Honour a reload the moment the kiosk can afford one.
 *
 * A request that cannot be met now is never dropped — that was the whole of the
 * bug: the shell forgets its question the instant it is answered, so a `false`
 * meant the menu item did nothing, for ever, with no way to tell it had been
 * refused. The page keeps the request instead.
 *
 * Idle rather than merely money-free, because by the time a charge has settled
 * there is a customer reading their order number, and what was asked for was a
 * reload — not for that screen to vanish out from under them.
 */
export function reloadWhenIdle(): void {
  const attempt = () => {
    if (currentlySafe()) window.location.reload()
    else setTimeout(attempt, 10_000)
  }
  attempt()
}

/**
 * Register the service worker and hold its update until the store can afford it.
 *
 * Polls rather than reacting, because the conditions are all local state and a
 * kiosk that becomes idle at 4am should take the update then rather than at the
 * next customer's first tap.
 */
export function registerServiceWorker(): () => void {
  if (!('serviceWorker' in navigator)) return () => {}

  let waiting: ServiceWorker | null = null
  let timer: ReturnType<typeof setInterval> | null = null

  const activateWhenIdle = () => {
    if (waiting === null) return
    if (!currentlySafe()) return
    waiting.postMessage({ type: 'skip-waiting' })
    waiting = null
  }

  void navigator.serviceWorker
    // The build id rides on the worker's URL; `sw.js` names its caches after
    // it, so an old deploy's assets are dropped on activate instead of
    // accumulating for the life of the machine.
    .register(`/sw.js?v=${__BUILD_ID__}`, { scope: '/' })
    .then((registration) => {
      const track = (worker: ServiceWorker | null) => {
        if (worker === null) return
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller !== null) {
            waiting = worker
            activateWhenIdle()
          }
        })
      }
      track(registration.waiting)
      registration.addEventListener('updatefound', () => track(registration.installing))
      timer = setInterval(activateWhenIdle, 5_000)
    })
    .catch(() => {
      // No service worker is a kiosk that works and does not cache. Not fatal.
    })

  // The controller changing means a new bundle is live; reload once, and only
  // when this page allowed it in the first place.
  let reloading = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return
    reloading = true
    window.location.reload()
  })

  return () => {
    if (timer !== null) clearInterval(timer)
  }
}

/**
 * The shell's own updater, gated the same way.
 *
 * `shell://reload-requested` carries a nonce and a watchdog: the shell asks,
 * waits, and reloads anyway. That is the point rather than the fallback — on a
 * kiosked machine with no taskbar, a page that has wedged and cannot answer is
 * a phone call. A page that *can* answer refuses only for money, and keeps the
 * request; `canReloadOnRequest` is why the two reloads do not share a rule.
 */
export function watchShellUpdates(): () => void {
  const unsubscribes: Promise<() => void>[] = []

  unsubscribes.push(
    listen<{ version: string }>('shell://update-ready', () => {
      const attempt = () => {
        if (currentlySafe()) {
          void applyUpdate().catch(() => {})
        } else {
          setTimeout(attempt, 10_000)
        }
      }
      attempt()
    })
  )

  unsubscribes.push(
    listen<{ id: number }>('shell://reload-requested', (payload) => {
      // The menu item behind this is a person standing at the machine, so the
      // gate is money and nothing else, and a refusal is a deferral this page
      // owns. Closing is not the same question and keeps the stricter rule.
      const now = reloadOnRequestSafe()
      void reloadVerdict(payload.id, now).catch(() => {})
      if (!now) reloadWhenIdle()
    })
  )

  unsubscribes.push(
    listen<{ id: number }>('shell://close-requested', (payload) => {
      void closeVerdict(payload.id, currentlySafe()).catch(() => {})
    })
  )

  // If the shell already holds an update from before this page loaded.
  void updateStatus()
    .then((status) => {
      if (status.held && currentlySafe()) void applyUpdate().catch(() => {})
    })
    .catch(() => {})

  return () => {
    for (const pending of unsubscribes) void pending.then((off) => off()).catch(() => {})
  }
}
